// Helpers over the assignment wire shape. Live here rather than in a feature
// folder because the Manifest board, the Plan queue and the order-detail panel
// all read them, and none should import another's lib.

import type { AssignmentSlotValue, Load } from './client';

/**
 * The minimum a row needs to expose to be labelled. Structural so it accepts
 * both `OrderTaskResource` (task-scoped reads) and `LoadResourceSummary` (inline
 * on a `LoadDto`) without converting first.
 */
export interface SlotBearing {
  slot: AssignmentSlotValue;
  slotIndex: number;
  isSubcontractor: boolean;
  externalReference?: string | null;
  referenceSnapshot?: string | null;
}

/**
 * Label for one assignment slot, or null when nothing fills it.
 *
 * <p>
 * Prefers the stored snapshot (what the unit was called when it was assigned)
 * over the free-text reference. A subcontracted slot with neither still reads as
 * assigned — falling back to "Subcontracted" — because a haulier who hasn't told
 * us the registration yet is not the same as no assignment at all.
 * </p>
 *
 * <p>
 * Slot 1 only. A drawbar's second trailer is in the data, but these grids have
 * one cell per slot; rendering "TR1 +1" is a display decision for later.
 * </p>
 */
export function slotLabelFrom(
  rows: readonly SlotBearing[],
  slot: AssignmentSlotValue,
): string | null {
  return slotLabel(rows.find((r) => r.slot === slot && r.slotIndex === 1));
}

/** Same rule, for a single row the caller has already located. */
export function slotLabel(row: SlotBearing | undefined): string | null {
  if (!row) return null;
  return (
    row.referenceSnapshot?.trim() ||
    row.externalReference?.trim() ||
    (row.isSubcontractor ? 'Subcontracted' : null)
  );
}

/** Convenience for a load, whose crew rides inline on the DTO. */
export const loadSlotLabel = (load: Load, slot: AssignmentSlotValue) =>
  slotLabelFrom(load.resources, slot);

/** True when any slot on the load is supplied by a subcontractor. */
export const isSubcontractedLoad = (load: Load) =>
  load.resources.some((r) => r.isSubcontractor);
